import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatDate, formatCOP, formatApiError } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import ConfirmDialog from "../components/ConfirmDialog";
import { useConfirm } from "../components/useConfirm";
import { toast } from "sonner";
import { Disclaimer } from "../components/Disclaimer";

const STATE_LABEL = {
  nivel2_paid: "Pago confirmado",
  nivel2_processing: "Generando kit documental",
  nivel2_needs_human_review: "Listo para revisión",
  nivel2_revision_requested: "Revisión solicitada",
  nivel2_delivered: "Kit documental entregado",
};

function Pill({ tone, children, testid }) {
  const tones = {
    ok: "bg-emerald-50 text-emerald-800 border-emerald-300",
    warn: "bg-amber-50 text-amber-900 border-amber-300",
    err: "bg-rose-50 text-rose-800 border-rose-300",
    info: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <span data-testid={testid} className={`inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider ${tones[tone] || tones.info}`}>
      {children}
    </span>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="mb-6 border border-gray-200 bg-white p-6" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">{title}</p>
      <div className="text-sm text-gray-800 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function SourcesList({ sources }) {
  if (!sources || sources.length === 0) return <p className="text-gray-500 italic">Sin fuentes</p>;
  return (
    <ul className="list-disc pl-5 text-xs">
      {sources.map((s, i) => (
        <li key={`${s.kind}-${s.ref}-${i}`}>
          <span className="uppercase text-gray-500 mr-2">{s.kind}</span>
          {s.ref}
          {s.url && <a href={s.url} className="text-primary underline ml-2" target="_blank" rel="noopener noreferrer">↗</a>}
        </li>
      ))}
    </ul>
  );
}

function LimitationsList({ limitations }) {
  if (!limitations || limitations.length === 0) return null;
  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <p className="font-medium text-xs uppercase tracking-wider text-gray-600 mb-1">Limitaciones:</p>
      <ul className="list-disc pl-5 text-xs text-gray-700">
        {limitations.map((l, i) => <li key={`lim-${i}`}>{l.description}</li>)}
      </ul>
    </div>
  );
}

function StudiesSection({ studies }) {
  if (!studies) return null;
  return (
    <Section title="1. Estudios Previos" testid="n2-section-studies">
      <div className="space-y-4">
        <div>
          <p className="font-semibold">Necesidad identificada:</p>
          <p>{studies.necesidad?.descripcion}</p>
          <p className="text-gray-700">{studies.necesidad?.justificacion}</p>
        </div>
        
        <div>
          <p className="font-semibold">Objeto contractual:</p>
          <p>{studies.objeto?.descripcion_objeto}</p>
          <p className="text-xs text-gray-600">
            Lugar: {studies.objeto?.lugar_ejecucion} · Plazo: {studies.objeto?.plazo_estimado_dias} días
          </p>
          {studies.objeto?.productos_entregables?.length > 0 && (
            <ul className="list-disc pl-5 text-xs mt-1">
              {studies.objeto.productos_entregables.map((p, i) => <li key={`prod-${p}-${i}`}>{p}</li>)}
            </ul>
          )}
        </div>
        
        <div>
          <p className="font-semibold">Presupuesto:</p>
          <p>Valor estimado: <strong>{studies.presupuesto?.valor_estimado_cop}</strong></p>
          <p className="text-xs">CDP: {studies.presupuesto?.certificado_disponibilidad} {studies.presupuesto?.cdp_numero && `(#${studies.presupuesto.cdp_numero})`}</p>
          <p className="text-gray-700">{studies.presupuesto?.analisis_mercado_resumen}</p>
        </div>
        
        <div>
          <p className="font-semibold">Modalidad de selección:</p>
          <p><strong>{studies.modalidad?.tipo}</strong></p>
          <p className="text-gray-700">{studies.modalidad?.justificacion}</p>
        </div>
        
        <div>
          <p className="font-semibold">Especificaciones técnicas (resumen):</p>
          <p className="text-gray-700">{studies.especificaciones_tecnicas_resumen}</p>
        </div>
        
        <SourcesList sources={studies.sources} />
        <LimitationsList limitations={studies.limitations} />
      </div>
    </Section>
  );
}

function SectorSection({ sector }) {
  if (!sector) return null;
  return (
    <Section title="2. Análisis del Sector" testid="n2-section-sector">
      <div className="space-y-4">
        <div>
          <p className="font-semibold">Resumen del sector:</p>
          <p className="text-gray-700">{sector.resumen_sector}</p>
        </div>
        
        <div>
          <p className="font-semibold">Estadísticas de mercado:</p>
          <ul className="list-disc pl-5 text-xs">
            <li>Contratos comparables: {sector.estadisticas_mercado?.n_contratos_comparables}</li>
            <li>Valor promedio: {sector.estadisticas_mercado?.valor_promedio_cop}</li>
            <li>Valor mediana: {sector.estadisticas_mercado?.valor_mediana_cop}</li>
            <li>Plazo promedio: {sector.estadisticas_mercado?.plazo_promedio_dias} días</li>
            <li>Modalidades frecuentes: {sector.estadisticas_mercado?.modalidades_frecuentes?.join(", ")}</li>
          </ul>
        </div>
        
        {sector.oferentes_identificados?.length > 0 && (
          <div>
            <p className="font-semibold">Oferentes identificados:</p>
            <ul className="list-disc pl-5">
              {sector.oferentes_identificados.map((o, i) => (
                <li key={`oferente-${o.nombre || i}`}>
                  <strong>{o.nombre}</strong> {o.nit && `(NIT: ${o.nit})`} — {o.contratos_previos} contratos previos
                  {o.fuente_secop_url && <a href={o.fuente_secop_url} className="text-primary underline text-xs ml-2" target="_blank" rel="noopener noreferrer">↗</a>}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <div>
          <p className="font-semibold">Concentración del mercado:</p>
          <p className="text-gray-700">{sector.concentracion_mercado}</p>
        </div>
        
        {sector.barreras_entrada?.length > 0 && (
          <div>
            <p className="font-semibold">Barreras de entrada:</p>
            <ul className="list-disc pl-5 text-xs">
              {sector.barreras_entrada.map((b, i) => <li key={`barr-${i}`}>{b}</li>)}
            </ul>
          </div>
        )}
        
        <SourcesList sources={sector.sources} />
        <LimitationsList limitations={sector.limitations} />
      </div>
    </Section>
  );
}

function RisksSection({ risks }) {
  if (!risks) return null;
  const categoryColors = {
    legal: "border-red-400",
    financiero: "border-amber-400",
    operacional: "border-blue-400",
    tecnico: "border-purple-400",
    reputacional: "border-pink-400",
    ambiental: "border-green-400",
  };
  
  return (
    <Section title="3. Matriz de Riesgos" testid="n2-section-risks">
      <div className="space-y-4">
        <p className="text-gray-700">{risks.resumen_matriz}</p>
        
        <div className="space-y-3">
          {risks.riesgos?.map((r, i) => (
            <div key={r.id || `risk-${i}`} className={`border-l-2 pl-3 ${categoryColors[r.categoria] || 'border-gray-400'}`}>
              <p className="font-semibold">
                {r.id} — {r.descripcion}
                <Pill tone="info">{r.categoria}</Pill>
              </p>
              <p className="text-xs text-gray-600">
                Probabilidad: {r.probabilidad} · Impacto: {r.impacto} · Nivel: {r.nivel_riesgo}
              </p>
              <p className="text-xs text-gray-600">Responsable: {r.responsable}</p>
              <p className="text-gray-700">Mitigación: {r.mitigacion}</p>
              {r.fuente && (
                <p className="text-xs text-gray-500 mt-1">
                  Fuente: {r.fuente.kind} - {r.fuente.ref}
                </p>
              )}
            </div>
          ))}
        </div>
        
        <SourcesList sources={risks.sources} />
        <LimitationsList limitations={risks.limitations} />
      </div>
    </Section>
  );
}

function InvitationSection({ invitation }) {
  if (!invitation) return null;
  return (
    <Section title="4. Invitación / Pliego" testid="n2-section-invitation">
      <div className="space-y-4">
        <div>
          <p className="font-semibold">Título del proceso:</p>
          <p>{invitation.titulo_proceso}</p>
        </div>
        
        <div>
          <p className="font-semibold">Entidad contratante:</p>
          <p>{invitation.entidad_contratante}</p>
        </div>
        
        <div>
          <p className="font-semibold">Objeto del contrato:</p>
          <p className="text-gray-700">{invitation.objeto_contrato}</p>
        </div>
        
        <div>
          <p className="font-semibold">Presupuesto oficial:</p>
          <p><strong>{invitation.presupuesto_oficial_cop}</strong></p>
          <p className="text-xs">Plazo: {invitation.plazo_ejecucion_dias} días</p>
        </div>
        
        {invitation.cronograma?.length > 0 && (
          <div>
            <p className="font-semibold">Cronograma:</p>
            <table className="w-full text-xs border border-gray-300 mt-2">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 p-2 text-left">Etapa</th>
                  <th className="border border-gray-300 p-2 text-left">Inicio</th>
                  <th className="border border-gray-300 p-2 text-left">Fin</th>
                </tr>
              </thead>
              <tbody>
                {invitation.cronograma.map((c, i) => (
                  <tr key={`cron-${c.etapa || i}`}>
                    <td className="border border-gray-300 p-2">{c.etapa}</td>
                    <td className="border border-gray-300 p-2">{c.fecha_inicio}</td>
                    <td className="border border-gray-300 p-2">{c.fecha_fin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {invitation.requisitos_habilitacion?.length > 0 && (
          <div>
            <p className="font-semibold">Requisitos de habilitación:</p>
            <ul className="list-disc pl-5 text-xs">
              {invitation.requisitos_habilitacion.map((r, i) => (
                <li key={r.id || `req-${i}`}>
                  {r.id} — {r.descripcion} (<Pill tone={r.cumple_si_no === "cumple" ? "ok" : "warn"}>{r.cumple_si_no}</Pill>)
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {invitation.criterios_evaluacion?.length > 0 && (
          <div>
            <p className="font-semibold">Criterios de evaluación:</p>
            <ul className="list-disc pl-5 text-xs">
              {invitation.criterios_evaluacion.map((c, i) => (
                <li key={c.id || `crit-${i}`}>
                  {c.id} — {c.descripcion} ({c.ponderacion_porcentaje})
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {invitation.garantias_requeridas?.length > 0 && (
          <div>
            <p className="font-semibold">Garantías requeridas:</p>
            <ul className="list-disc pl-5 text-xs">
              {invitation.garantias_requeridas.map((g, i) => <li key={`gar-${g}-${i}`}>{g}</li>)}
            </ul>
          </div>
        )}
        
        <div>
          <p className="font-semibold">Forma de pago:</p>
          <p className="text-gray-700">{invitation.forma_pago}</p>
        </div>
        
        <div>
          <p className="font-semibold">Supervisión / Interventoría:</p>
          <p className="text-gray-700">{invitation.supervision_interventoria}</p>
        </div>
        
        <SourcesList sources={invitation.sources} />
        <LimitationsList limitations={invitation.limitations} />
      </div>
    </Section>
  );
}

function ChecklistSection({ checklist }) {
  if (!checklist) return null;
  
  const statusTone = {
    completo: "ok",
    pendiente: "warn",
    no_aplica: "info",
  };
  
  const itemsByCategory = {};
  checklist.items?.forEach(item => {
    if (!itemsByCategory[item.categoria]) {
      itemsByCategory[item.categoria] = [];
    }
    itemsByCategory[item.categoria].push(item);
  });
  
  return (
    <Section title="5. Checklist Legal" testid="n2-section-checklist">
      <div className="space-y-4">
        <p className="text-gray-700">{checklist.resumen_completitud}</p>
        
        {Object.entries(itemsByCategory).map(([cat, items]) => (
          <div key={cat}>
            <p className="font-semibold uppercase text-xs text-gray-600">{cat}:</p>
            <ul className="list-disc pl-5 text-xs space-y-1">
              {items.map((item, idx) => (
                <li key={item.id || `${cat}-${idx}`}>
                  {item.id ? `${item.id} — ` : ""}{item.descripcion || item.nombre} <Pill tone={statusTone[item.status] || statusTone[item.estado] || "info"}>{item.status || item.estado}</Pill>
                  {item.referencia_legal && <span className="text-gray-500 ml-2">({item.referencia_legal})</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
        
        <SourcesList sources={checklist.sources} />
        <LimitationsList limitations={checklist.limitations} />
      </div>
    </Section>
  );
}

export default function AdminCaseKitDocumentalPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [kit, setKit] = useState(null);
  const [caseState, setCaseState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versions, setVersions] = useState([]);

  // Iter24.d: confirm dialog reutilizable
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  const fetchKit = useCallback(async () => {
    try {
      setLoading(true);
      const { data: res } = await api.get(`/admin/cases/${id}/nivel2/kit-documental`);
      setKit(res.kit);
      setCaseState(res.case_state);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchVersions = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/admin/cases/${id}/nivel2/kit-documental/versions`);
      setVersions(res.versions || res.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [id]);

  useEffect(() => {
    fetchKit();
  }, [fetchKit]);

  const handleRegenerate = async () => {
    const ok = await confirm({
      title: "Regenerar Kit Documental",
      description: "¿Confirmas regenerar el Kit Documental N2? Esto creará una nueva versión y reemplazará el contenido actual del Kit.",
      confirmLabel: "Sí, regenerar",
      testIdPrefix: "n2-regenerate-confirm",
    });
    if (!ok) return;

    try {
      setRegenerating(true);
      await api.post(`/admin/cases/${id}/nivel2/generate`, { force: true });
      toast.success("Regeneración iniciada. Refresca en unos segundos.");
      setTimeout(fetchKit, 3000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRegenerating(false);
    }
  };

  const handleVersionsClick = async () => {
    await fetchVersions();
    setShowVersionsModal(true);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <p>Cargando kit documental...</p>
      </div>
    );
  }

  if (!kit) {
    return (
      <div className="container mx-auto py-8">
        <p>Kit Documental N2 no encontrado para este caso.</p>
        <Link to={`/admin/cases/${id}`} className="text-primary underline">← Volver al caso</Link>
      </div>
    );
  }

  const validated = kit.validated_output;
  const warnings = kit.validation_warnings || [];
  const prohibitedDetected = validated?.prohibited_phrases_detected || [];

  return (
    <div className="container mx-auto py-8" data-testid="admin-n2-kit-page">
      {/* Header */}
      <div className="bg-white border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Link to={`/admin/cases/${id}`} className="text-primary text-sm underline">← Volver al caso</Link>
            <h1 className="text-2xl font-bold mt-2">Kit Documental Nivel 2</h1>
            <p className="text-sm text-gray-600">
              Caso: {id} · Estado: <Pill tone="info">{STATE_LABEL[caseState] || caseState}</Pill>
            </p>
          </div>
          <div className="flex gap-2">
            {user?.role === "admin" && (
              <>
                <Button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  variant="outline"
                  size="sm"
                  data-testid="n2-regenerate-btn"
                >
                  {regenerating ? "Regenerando..." : "Regenerar"}
                </Button>
                <Button
                  onClick={handleVersionsClick}
                  variant="outline"
                  size="sm"
                >
                  Historial de versiones
                </Button>
              </>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase text-gray-500">Versión</p>
            <p className="font-semibold">{kit.version}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Modelo usado</p>
            <p className="font-semibold">{kit.model_used}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Costo estimado</p>
            <p className="font-semibold">${validated?.cost_usd_estimated?.toFixed(4) || "0.00"} USD</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Generado</p>
            <p className="font-semibold">{formatDate(kit.created_at)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Estado de revisión</p>
            <p className="font-semibold">
              <Pill tone={validated?.human_review_status === "approved" ? "ok" : "warn"}>
                {validated?.human_review_status}
              </Pill>
            </p>
          </div>
        </div>
      </div>

      {/* Warnings panel */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 p-4 mb-6" data-testid="n2-warnings-panel">
          <p className="font-semibold text-amber-900 mb-2">⚠️ Advertencias ({warnings.length})</p>
          <ul className="list-disc pl-5 text-sm text-amber-800">
            {warnings.map((w, i) => <li key={`warn-${i}`}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Prohibited phrases detected */}
      {prohibitedDetected.length > 0 && (
        <div className="bg-rose-50 border border-rose-300 p-4 mb-6">
          <p className="font-semibold text-rose-900 mb-2">🚨 Frases prohibidas detectadas ({prohibitedDetected.length})</p>
          <ul className="list-disc pl-5 text-sm text-rose-800">
            {prohibitedDetected.map((p, i) => <li key={`proh-${p}-${i}`}>{p}</li>)}
          </ul>
        </div>
      )}

      {/* 5 Sections */}
      <StudiesSection studies={validated?.studies} />
      <SectorSection sector={validated?.sector} />
      <RisksSection risks={validated?.risks} />
      <InvitationSection invitation={validated?.invitation} />
      <ChecklistSection checklist={validated?.checklist} />

      {/* Disclaimer */}
      <Disclaimer text={validated?.disclaimer} />

      {/* Versions modal */}
      {showVersionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Historial de versiones</h2>
              <button onClick={() => setShowVersionsModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            
            {versions.length === 0 ? (
              <p className="text-gray-600">No hay versiones disponibles.</p>
            ) : (
              <ul className="space-y-2">
                {versions.map(v => (
                  <li key={v.id} className="border border-gray-200 p-3">
                    <p className="font-semibold">Versión {v.version} {v.is_current && <Pill tone="ok">Actual</Pill>}</p>
                    <p className="text-xs text-gray-600">
                      Modelo: {v.model_used} · Creado: {formatDate(v.created_at)}
                    </p>
                    {v.validation_warnings?.length > 0 && (
                      <p className="text-xs text-amber-700">Warnings: {v.validation_warnings.length}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
